import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Loader2,
  Cable,
  GitBranch,
  AlertTriangle,
  Play,
  FileJson,
  CheckCircle2
} from "lucide-react";
import {
  getWorkflow,
  updateWorkflow,
  saveWorkflowVersion,
  getWorkflowRuns
} from "../../services/workflowService";
import { canEditWorkflows } from "../../services/authService";
import { connectStep, deleteStep, disconnectEdge, addStep } from "../../lib/workflowOps";
import { reachableFrom } from "../../lib/graph";
import { useEndpoints } from "../../hooks/useEndpoints";
import { LoadingState, ErrorState } from "../ui/LoadingStates";
import GraphView from "./GraphView";
import ConfigRail from "./ConfigRail";
import StepDrawer from "./StepDrawer";
import RunVisualizer from "./RunVisualizer";

const deepCopy = (o) => JSON.parse(JSON.stringify(o || {}));
const rowsToObj = (rows) =>
  rows.reduce((acc, { key, value }) => {
    if (key.trim()) acc[key.trim()] = value;
    return acc;
  }, {});

/**
 * Workflow Studio — canvas-first visual editor (left config rail + drag-and-drop
 * canvas + right step drawer). Native orbit-ux styling. Admin/editor only.
 */
export default function WorkflowStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { endpoints, loading: epLoading } = useEndpoints();

  const [workflow, setWorkflow] = useState(null);
  const [latestRun, setLatestRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [draft, setDraft] = useState(null); // { name, description, definition, outputsRows }
  const [selectedStep, setSelectedStep] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [connectError, setConnectError] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [runOpen, setRunOpen] = useState(false);

  const editable = canEditWorkflows();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getWorkflow(id);
    if (res.success && res.data) {
      setWorkflow(res.data);
      const defn = res.data.latest_definition || {
        start_step: "",
        steps: {},
        inputs: {},
        outputs: {}
      };
      setDraft({
        name: res.data.name,
        description: res.data.description || "",
        definition: deepCopy(defn),
        outputsRows: Object.entries(defn.outputs || {}).map(([key, value]) => ({
          key,
          value: String(value)
        }))
      });
    } else {
      setError(res.message || "Failed to load workflow");
    }
    // Overlay the latest run's per-step status on the canvas (best-effort).
    const runsRes = await getWorkflowRuns(id);
    if (runsRes.success && Array.isArray(runsRes.data) && runsRes.data.length > 0) {
      setLatestRun(runsRes.data[0]);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Viewers don't get the studio — send them to the read-only detail.
  useEffect(() => {
    if (!editable) navigate(`/workflows/${id}`, { replace: true });
  }, [editable, id, navigate]);

  const setDef = (updater) =>
    setDraft((d) => ({ ...d, definition: updater(d.definition) }));

  const flashConnectError = (msg) => {
    setConnectError(msg);
    setTimeout(() => setConnectError(null), 4000);
  };

  const handleConnect = (source, target) => {
    const res = connectStep(draft.definition, source, target);
    if (res.ok) setDef(() => res.def);
    else flashConnectError(res.error);
  };
  const handleDeleteStep = (name) => {
    setDef((def) => deleteStep(def, name));
    if (selectedStep === name) setSelectedStep(null);
  };
  const handleDeleteEdge = (edgeId) => setDef((def) => disconnectEdge(def, edgeId));
  const handleAddStep = (type) => {
    const { def, name } = addStep(draft.definition, type);
    setDef(() => def);
    setSelectedStep(name);
  };
  const handleChangeStep = (updated) =>
    setDef((def) => ({
      ...def,
      steps: { ...def.steps, [selectedStep]: updated }
    }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    if (
      draft.name !== workflow.name ||
      draft.description !== (workflow.description || "")
    ) {
      const r = await updateWorkflow(id, {
        name: draft.name,
        description: draft.description
      });
      if (!r.success) {
        setSaveError(r.message);
        setSaving(false);
        return;
      }
    }
    const newDef = { ...draft.definition, outputs: rowsToObj(draft.outputsRows) };
    const rv = await saveWorkflowVersion(id, newDef);
    setSaving(false);
    if (!rv.success) {
      setSaveError(rv.message);
      return;
    }
    navigate(`/workflows/${id}`);
  };

  if (loading || !draft) {
    return (
      <div className="flex-1 bg-[#FCFCFC]">
        <LoadingState message="Loading studio…" size="large" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex-1 p-8 bg-[#FCFCFC]">
        <ErrorState message={error} />
      </div>
    );
  }

  const def = draft.definition;
  const selStep = selectedStep ? def.steps?.[selectedStep] : null;
  const stepNames = Object.keys(def.steps || {});

  // Lint — things to fix before this runs (mirrors the prototype's Ready chip).
  const issues = [];
  const reachable = reachableFrom(def.steps || {}, def.start_step);
  Object.entries(def.steps || {}).forEach(([name, s]) => {
    if (s.type === "endpoint_call" && !s.endpoint_id) issues.push(`"${name}" has no endpoint`);
    if (s.type === "branch" && (s.branches || []).length === 0 && !s.default_next)
      issues.push(`"${name}" branch has no routes`);
    if (name !== def.start_step && !reachable.has(name)) issues.push(`"${name}" isn't reachable`);
  });
  if (!def.start_step && stepNames.length > 0) issues.push("No start step set");

  return (
    <div className="flex-1 flex flex-col h-screen overflow-hidden bg-white">
      {/* Header */}
      <div className="shrink-0 px-6 py-3 border-b border-gray-200 flex items-center gap-3">
        <button
          onClick={() => navigate(`/workflows/${id}`)}
          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
          title="Back to workflow"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <input
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          className="text-lg font-bold text-gray-900 border border-transparent hover:border-gray-200 focus:border-blue-500 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-md"
        />
        <span className="text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full font-medium">
          Workflow Studio
        </span>
        <span className="text-xs text-gray-400">v{workflow.latest_version}</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowJson((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg ${
              showJson ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <FileJson className="w-4 h-4" /> JSON
          </button>
          {issues.length === 0 ? (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700">
              <CheckCircle2 className="w-3.5 h-3.5" /> Ready
            </span>
          ) : (
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-700" title={issues.join("\n")}>
              {issues.length} issue{issues.length > 1 ? "s" : ""}
            </span>
          )}
          <button
            onClick={() => navigate(`/workflows/${id}`)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save version
          </button>
          <button
            onClick={() => setRunOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800"
          >
            <Play className="w-4 h-4 fill-current" /> Run
          </button>
        </div>
      </div>

      {saveError && (
        <div className="shrink-0 px-6 py-2 bg-red-50 text-red-700 text-sm border-b border-red-100">
          {saveError}
        </div>
      )}

      {/* Body: rail + canvas + drawer */}
      <div className="flex-1 flex overflow-hidden">
        <ConfigRail
          definition={def}
          outputsRows={draft.outputsRows}
          editable={editable}
          onStartChange={(name) => setDef((d) => ({ ...d, start_step: name }))}
          onOutputsChange={(rows) => setDraft((d) => ({ ...d, outputsRows: rows }))}
          onInputsChange={(inputs) => setDef((d) => ({ ...d, inputs }))}
        />

        {/* Canvas column */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[#FCFCFC]">
          {showJson ? (
            <JsonEditor def={def} onChange={(d) => setDef(() => d)} />
          ) : (
            <>
              <div className="shrink-0 px-4 py-2 border-b border-gray-200 bg-white flex items-center gap-2">
                <button
                  onClick={() => handleAddStep("endpoint_call")}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  <Cable className="w-3.5 h-3.5" /> Call an API
                </button>
                <button
                  onClick={() => handleAddStep("branch")}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 rounded-lg hover:bg-violet-100"
                >
                  <GitBranch className="w-3.5 h-3.5" /> Add branch
                </button>
                <span className="text-xs text-gray-400 ml-1">
                  Drag from a node’s right dot to wire · click a node to edit · Delete removes
                </span>
                {connectError && (
                  <span className="ml-auto flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                    <AlertTriangle className="w-3.5 h-3.5" /> {connectError}
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                {stepNames.length > 0 ? (
                  <GraphView
                    definition={def}
                    endpoints={endpoints}
                    run={latestRun}
                    workflowId={id}
                    selectedStep={selectedStep}
                    onSelectStep={setSelectedStep}
                    interactive
                    onConnectStep={handleConnect}
                    onDeleteStep={handleDeleteStep}
                    onDeleteEdge={handleDeleteEdge}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-gray-400">
                    No steps yet — add an endpoint or branch to start.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {selStep && (
          <StepDrawer
            stepName={selectedStep}
            step={selStep}
            allStepNames={stepNames}
            endpoints={endpoints}
            endpointsLoading={epLoading}
            editable={editable}
            onChangeStep={handleChangeStep}
            onClose={() => setSelectedStep(null)}
          />
        )}
      </div>

      {runOpen && (
        <RunVisualizer workflow={workflow} onClose={() => setRunOpen(false)} />
      )}
    </div>
  );
}

function JsonEditor({ def, onChange }) {
  const [text, setText] = useState(JSON.stringify(def, null, 2));
  const [err, setErr] = useState(null);
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-200 bg-white text-xs text-gray-500 flex items-center gap-2">
        <FileJson className="w-3.5 h-3.5" /> Raw definition — changes apply when valid JSON.
      </div>
      <textarea
        value={text}
        spellCheck={false}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(JSON.parse(e.target.value));
            setErr(null);
          } catch (ex) {
            setErr(String(ex.message));
          }
        }}
        className="flex-1 font-mono text-xs p-4 resize-none focus:outline-none bg-white"
      />
      {err && (
        <div className="bg-red-50 text-red-700 text-xs px-4 py-2 border-t border-red-200">{err}</div>
      )}
    </div>
  );
}
